import {
  Activity,
  Heart,
  Zap,
  TrendingUp,
  Moon,
  Calendar,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Brain,
  RefreshCw,
  Droplets,
  Wind,
  Thermometer,
} from "lucide-react";
import welcomeBg from "../../assets/homebg.png";
import { Card } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { Button } from "../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { message } from "antd";
import BottomNavBar from "../../components/BottomNavBar";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";


import fitnessService, { FitnessData } from "../../services/FitnessService";
import fitbitAuthService from "../../services/FitbitAuthService";

const FitnessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();





  const [searchParams] = useSearchParams();
  const [isProcessingFitbit, setIsProcessingFitbit] = useState(false);
  const hasExchangedCode = useRef(false);

  // Handle Fitbit OAuth Callback if parameters are present in URL
  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (code && state && !isProcessingFitbit && !hasExchangedCode.current) {
      const handleFitbitCallback = async () => {
        hasExchangedCode.current = true;
        setIsProcessingFitbit(true);
        const hide = message.loading("Connecting your Fitbit account...", 0);

        try {
          console.log("[FitnessPage] Detected Fitbit callback, exchanging code...");
          await fitbitAuthService.exchangeCode(code, state);
          message.success("Fitbit connected successfully!");

          // Force a full page refresh back to the clean URL (without code/state)
          // This ensures all services and components reset correctly.
          console.log("[FitnessPage] Connection successful, reloading clean URL...");
          hide();
          window.location.href = window.location.pathname;
        } catch (error) {
          console.error("[FitnessPage] Fitbit exchange failed:", error);
          message.error("Failed to connect Fitbit. Please try again.");
          hide();
          setIsProcessingFitbit(false);
        }
      };

      handleFitbitCallback();
    }
  }, [searchParams]);


  // Initialize with passed date from navigation state or today
  const initialDate = location.state?.selectedDateObj
    ? location.state.selectedDateObj
    : location.state?.selectedDate
      ? (() => {
        const [year, month, day] = location.state.selectedDate
          .split("-")
          .map(Number);
        return new Date(year, month - 1, day);
      })()
      : new Date();

  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);
  const [currentMonth, setCurrentMonth] = useState<Date>(initialDate);
  const [fitnessData, setFitnessData] = useState<FitnessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [fitbitNotConnected, setFitbitNotConnected] = useState(false);
  const [stressData, setStressData] = useState<{
    score: number;
    level: string;
  } | null>(null);

  // Get days in current month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSameDate = (date1: Date, date2: Date) => {
    return date1.toDateString() === date2.toDateString();
  };

  const fetchFitnessData = async (date: string) => {
    try {
      setLoading(true);
      console.log(`[FitnessPage] Fetching data for date: ${date}`);
      const result = (await fitnessService.getDailyFitnessData(date)) as any;
      console.log("[FitnessPage] API Result:", result);

      // Check if response contains Fitbit token missing error (401)
      const is401 =
        (result?.status === "401" || result?.status === 401) &&
        (result?.error === "Fitbit token not found" ||
          (result?.message || "").toLowerCase().includes("connect fitbit"));

      if (is401) {
        console.log("[FitnessPage] Fitbit not connected (401 detected in response)");
        setFitbitNotConnected(true);
        setFitnessData(null);
        setLoading(false);
        return;
      }

      // If it's not a 401, we want to show the data screen (dashboard)
      // even if there was an error or the data is empty.
      console.log("[FitnessPage] Setting fitbitNotConnected to false");
      setFitbitNotConnected(false);

      // Handle successful data or fall back to zeros
      if (result && result.data) {
        console.log("[FitnessPage] Data found, setting fitnessData");
        setFitnessData(result.data);
        if (result.data.stress_score !== undefined && result.data.stress_level) {
          setStressData({
            score: result.data.stress_score,
            level: result.data.stress_level,
          });
        }
      } else {
        console.log("[FitnessPage] No data in result, using fallback zeros");
        // Successful API call but no data, or unsuccessful (non-401)
        const emptyFitnessData: FitnessData = {
          steps: 0,
          steps_goal: 10000,
          calories_burned: 0,
          calories_goal: 2500,
          active_zone_minutes: 0,
          active_zone_minutes_goal: 30,
          resting_heart_rate: 0,
          sleep_minutes: 0,
          sleep_goal: 8,
          weight_kg: 0,
          distance_km: 0,
          floors: 0,
          stress_score: 0,
          stress_level: "Neutral",
          weekly_steps: { this_week: [] },
        };
        setFitnessData(emptyFitnessData);
        setStressData(null);
      }
    } catch (error: any) {
      console.error("[FitnessPage] Error in fetchFitnessData:", error);
      // Check if error is specifically Fitbit token not found (401)
      const errorResponse = error?.response?.data || {};
      const errorStatus = error?.response?.status || error?.status;
      const errorMessage = errorResponse.error || errorResponse.message || error?.message || "";

      console.log(`[FitnessPage] Error status: ${errorStatus}, message: ${errorMessage}`);

      if (
        (errorStatus === 401 || errorStatus === "401") &&
        (errorMessage.toLowerCase().includes("token not found") ||
          errorMessage.toLowerCase().includes("connect fitbit"))
      ) {
        console.log("[FitnessPage] Fitbit not connected (401 caught)");
        setFitbitNotConnected(true);
        setFitnessData(null);
      } else {
        console.log("[FitnessPage] Other error, showing empty dashboard");
        // For other errors (e.g. 500 or network), show empty dashboard
        const emptyData: FitnessData = {
          steps: 0,
          steps_goal: 10000,
          calories_burned: 0,
          calories_goal: 2500,
          active_zone_minutes: 0,
          active_zone_minutes_goal: 30,
          resting_heart_rate: 0,
          sleep_minutes: 0,
          sleep_goal: 8,
          weight_kg: 0,
          distance_km: 0,
          floors: 0,
          stress_score: 0,
          stress_level: "Neutral",
          weekly_steps: { this_week: [] },
        };
        setFitbitNotConnected(false);
        setFitnessData(emptyData);
        setStressData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFitbitLogin = async () => {
    await fitbitAuthService.loginRedirect((err: string) => {
      message.error(err);
    });
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;

      await fitnessService.syncFitbitData(dateStr);
      await fetchFitnessData(dateStr);
    } catch (error: any) {
      // Still fetch data even if sync had issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;
      await fetchFitnessData(dateStr);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    // 🔧 Fix: Skip fetching data if we're in the middle of a Fitbit callback
    // The handleFitbitCallback effect will handle the refresh/load.
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    if (code && state) {
      console.log("[FitnessPage] Skipping initial fetch because Fitbit callback is in progress");
      return;
    }

    // Auto-refresh token if expired on page load
    const initializeFitbit = async () => {
      if (fitnessService.hasFitbitConnected()) {
        try {
          await fitbitAuthService.ensureValidToken();
        } catch (error) {
          // Token refresh failed silently
        }
      }
    };

    initializeFitbit();

    // 🔧 Fix: Use local timezone instead of UTC to prevent date shift
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    fetchFitnessData(dateStr);
  }, [selectedDate, searchParams]);

  const getActivityMetrics = () => {
    if (!fitnessData) {
      return [];
    }

    // Get goals from nested goals object or direct properties
    const stepsGoal =
      fitnessData.goals?.steps || fitnessData.steps_goal || 10000;
    const caloriesGoal =
      fitnessData.goals?.calories || fitnessData.calories_goal || 2500;
    const activeMinutesGoal =
      fitnessData.goals?.active_zone_minutes ||
      fitnessData.active_zone_minutes_goal ||
      30;

    const stepsProgress = Math.round(
      ((fitnessData.steps || 0) / stepsGoal) * 100
    );
    const caloriesProgress = Math.round(
      ((fitnessData.calories_burned || 0) / caloriesGoal) * 100
    );
    const activeMinutesProgress = Math.round(
      ((fitnessData.active_zone_minutes || 0) / activeMinutesGoal) * 100
    );

    return [
      {
        icon: Activity,
        label: "Steps",
        value: (fitnessData.steps || 0).toLocaleString(),
        unit: `of ${stepsGoal.toLocaleString()}`,
        progress: stepsProgress,
        color: "bg-blue-500",
        lightColor: "bg-blue-50",
      },
      {
        icon: Heart,
        label: "Heart Rate",
        value: fitnessData.resting_heart_rate || "--",
        unit: "BPM",
        progress: null,
        color: "bg-red-500",
        lightColor: "bg-red-50",
      },
      {
        icon: Zap,
        label: "Calories Burned",
        value: (fitnessData.calories_burned || 0).toLocaleString(),
        unit: `of ${caloriesGoal.toLocaleString()} cal`,
        progress: caloriesProgress,
        color: "bg-orange-500",
        lightColor: "bg-orange-50",
      },
      {
        icon: TrendingUp,
        label: "Active Minutes",
        value: fitnessData.active_zone_minutes || 0,
        unit: `of ${activeMinutesGoal} min`,
        progress: activeMinutesProgress,
        color: "bg-green-500",
        lightColor: "bg-green-50",
      },
    ];
  };

  const activityMetrics = getActivityMetrics();

  const getWeeklyActivity = () => {
    if (!fitnessData?.weekly_steps?.this_week) {
      return [];
    }

    return fitnessData.weekly_steps.this_week.map(dayData => ({
      day: dayData.day,
      steps: dayData.steps,
      progress: Math.round(
        ((dayData.steps || 0) / (fitnessData.steps_goal || 6000)) * 100
      ),
    }));
  };

  const weeklyActivity = getWeeklyActivity();



  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-lg text-muted-foreground">
              Loading fitness data...
            </div>
          </div>
        ) : fitbitNotConnected ? (
          <div className="flex flex-col items-center justify-center px-4 py-12">
            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
              <Activity className="h-10 w-10 text-orange-500" />
            </div>
            <h2 className="mb-2 text-center text-2xl font-bold text-foreground">
              Connect Fitbit to View Data
            </h2>
            <p className="mb-6 max-w-md text-center text-sm text-muted-foreground">
              You haven't connected your Fitbit yet. Connect now to
              automatically track your activities, steps, calories, sleep, and
              get personalized health insights.
            </p>
            <Button
              onClick={handleFitbitLogin}
              className="mb-4 bg-orange-500 hover:bg-orange-600"
            >
              <Activity className="mr-2 h-4 w-4" />
              Connect Now
            </Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between pt-2">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {isToday(selectedDate)
                    ? "Today's Activity"
                    : "Daily Activity"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {selectedDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {/* Sync Fitbit Button - Show for all past and current dates */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSync}
                  disabled={syncing || loading}
                  className="h-10 rounded-xl border-border bg-card px-3 hover:bg-muted"
                  title="Sync Fitbit data for selected date"
                >
                  <RefreshCw
                    className={`h-4 w-4 text-orange-500 ${syncing ? "animate-spin" : ""
                      }`}
                  />
                  <span className="ml-2 text-sm font-medium text-muted-foreground">
                    {syncing ? "Syncing..." : "Sync"}
                  </span>
                </Button>

                {/* Calendar Button */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-xl border-border bg-card"
                    >
                      <Calendar className="h-5 w-5 text-orange-500" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="z-50 w-80 rounded-lg border bg-card p-0 shadow-lg"
                    align="end"
                  >
                    {/* Calendar Header */}
                    <div className="flex items-center justify-between border-b border-border p-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={goToPreviousMonth}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <h2 className="text-sm font-semibold">
                        {currentMonth.toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })}
                      </h2>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={goToNextMonth}
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Calendar Grid */}
                    <div className="p-3">
                      {/* Days of Week Header */}
                      <div className="mb-2 grid grid-cols-7 gap-1">
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                          day => (
                            <div
                              key={day}
                              className="p-2 text-center text-xs font-medium text-muted-foreground"
                            >
                              {day}
                            </div>
                          )
                        )}
                      </div>

                      {/* Calendar Days */}
                      <div className="grid grid-cols-7 gap-1">
                        {getDaysInMonth(currentMonth).map((day, index) => (
                          <div key={index} className="aspect-square">
                            {day ? (
                              <button
                                onClick={() => {
                                  // Check if date is in future - same logic as meal page
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  const dateObj = new Date(day);
                                  dateObj.setHours(0, 0, 0, 0);
                                  const isFuture = dateObj > today;

                                  if (!isFuture) {
                                    setSelectedDate(day);
                                  }
                                }}
                                disabled={(() => {
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  const dateObj = new Date(day);
                                  dateObj.setHours(0, 0, 0, 0);
                                  return dateObj > today;
                                })()}
                                className={`flex h-full w-full items-center justify-center rounded-md text-xs transition-colors ${(() => {
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  const dateObj = new Date(day);
                                  dateObj.setHours(0, 0, 0, 0);
                                  const isFuture = dateObj > today;

                                  return isFuture
                                    ? "cursor-not-allowed text-muted-foreground hover:bg-transparent"
                                    : isSameDate(day, selectedDate)
                                      ? "bg-orange-500 text-white"
                                      : isToday(day)
                                        ? "bg-orange-100 font-medium text-orange-700"
                                        : "hover:bg-muted";
                                })()}`}
                                title={(() => {
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  const dateObj = new Date(day);
                                  dateObj.setHours(0, 0, 0, 0);
                                  return dateObj > today
                                    ? "Future dates not available"
                                    : "";
                                })()}
                              >
                                {day.getDate()}
                              </button>
                            ) : (
                              <div />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Selected Date Display */}
                    <div className="border-t border-border bg-muted p-3">
                      <p className="text-center text-xs text-muted-foreground">
                        Viewing activity for {formatDate(selectedDate)}
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {activityMetrics.map((metric, i) => {
                const Icon = metric.icon;
                const routes = [
                  `/glucera${GluceraNavigationRoutes.STEPS_DETAIL}`,
                  `/glucera${GluceraNavigationRoutes.HEART_RATE_DETAIL}`,
                  `/glucera${GluceraNavigationRoutes.CALORIES_DETAIL}`,
                  `/glucera${GluceraNavigationRoutes.ACTIVE_MINUTES_DETAIL}`,
                ];
                return (
                  <Card
                    key={i}
                    className="cursor-pointer rounded-xl border border-border bg-card p-3 shadow-lg transition-shadow hover:shadow-xl sm:p-4"
                    onClick={() => {
                      // Pass selected date to all detail pages
                      const year = selectedDate.getFullYear();
                      const month = String(
                        selectedDate.getMonth() + 1
                      ).padStart(2, "0");
                      const day = String(selectedDate.getDate()).padStart(
                        2,
                        "0"
                      );
                      const dateStr = `${year}-${month}-${day}`;
                      navigate(routes[i], {
                        state: {
                          selectedDate: dateStr,
                          selectedDateObj: selectedDate,
                        },
                      });
                    }}
                  >
                    <div
                      className={`h-8 w-8 rounded-xl ${metric.color}/20 mb-4 flex items-center justify-center`}
                    >
                      <Icon
                        className={`h-4 w-4 ${metric.color.replace("bg-", "text-")}`}
                      />
                    </div>
                    <div className="space-y-3">
                      <div>
                        <div className="text-2xl font-bold text-foreground tracking-tight sm:text-3xl">
                          {metric.value}
                        </div>
                        <div className="text-sm font-medium text-muted-foreground">
                          {metric.unit}
                        </div>
                      </div>
                      {metric.progress !== null && (
                        <Progress
                          value={metric.progress}
                          className="h-2 bg-secondary"
                        />
                      )}
                      <p className="text-lg font-semibold text-foreground pt-1 sm:text-xl">
                        {metric.label}
                      </p>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Sleep Card */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900">
                    <Moon className="h-6 w-6 text-blue-500 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      Sleep
                    </h3>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-foreground">
                        {fitnessData
                          ? ((fitnessData.sleep_minutes || 0) / 60).toFixed(1)
                          : "--"}
                      </span>
                      <span className="text-sm text-muted-foreground">hrs</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      of{" "}
                      {fitnessData?.goals?.sleep ||
                        fitnessData?.sleep_goal ||
                        8}{" "}
                      hrs goal
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900"
                  onClick={() => {
                    const year = selectedDate.getFullYear();
                    const month = String(selectedDate.getMonth() + 1).padStart(
                      2,
                      "0"
                    );
                    const day = String(selectedDate.getDate()).padStart(2, "0");
                    const dateStr = `${year}-${month}-${day}`;
                    navigate(
                      `/glucera${GluceraNavigationRoutes.SLEEP_DETAIL}`,
                      {
                        state: {
                          selectedDate: dateStr,
                          selectedDateObj: selectedDate,
                        },
                      }
                    );
                  }}
                >
                  View Details
                </Button>
              </div>
            </Card>
            <Card
              className="cursor-pointer rounded-2xl border border-border bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 p-5 shadow-lg dark:from-orange-950 dark:via-yellow-950 dark:to-red-950"
              onClick={() => {
                const year = selectedDate.getFullYear();
                const month = String(selectedDate.getMonth() + 1).padStart(
                  2,
                  "0"
                );
                const day = String(selectedDate.getDate()).padStart(2, "0");
                const dateStr = `${year}-${month}-${day}`;
                navigate(
                  `/glucera${GluceraNavigationRoutes.STRESS_SCORE_DETAIL}`,
                  {
                    state: {
                      selectedDate: dateStr,
                      selectedDateObj: selectedDate,
                    },
                  }
                );
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-900">
                    <Brain className="h-6 w-6 text-orange-500 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      Stress Score
                    </h3>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-foreground">
                        {stressData?.score?.toFixed(1) || "--"}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        / 100
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {stressData?.level || "No data"}
                    </p>
                  </div>
                </div>
                {/* <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-orange-600 hover:bg-orange-50"
              onClick={() => {
                navigate(`/glucera${GluceraNavigationRoutes.SLEEP_DETAIL}`);
              }}
            >
              View Details
            </Button> */}
              </div>
            </Card>
            {/* Body Metrics */}
            <div className="grid grid-cols-2 gap-2">
              <Card className="rounded-xl border border-border bg-card p-3 text-center shadow-lg">
                <div className="text-xl font-bold text-foreground">
                  {fitnessData?.preferred_weight_unit === "lbs"
                    ? fitnessData?.weight_lbs
                      ? fitnessData.weight_lbs.toFixed(1)
                      : "--"
                    : fitnessData?.weight_kg
                      ? fitnessData.weight_kg.toFixed(1)
                      : "--"}
                </div>
                <div className="mb-1 text-xs text-muted-foreground">
                  {fitnessData?.preferred_weight_unit === "lbs" ? "lbs" : "kg"}
                </div>
                <div className="mb-1 text-xs text-muted-foreground">
                  weight
                </div>
                {(() => {
                  const isLbs = fitnessData?.preferred_weight_unit === "lbs";
                  const targetVal = isLbs
                    ? fitnessData?.target_weight_lbs
                    : fitnessData?.target_weight_kg;

                  if (targetVal && targetVal > 0) {
                    return (
                      <div className="text-xs text-blue-500">
                        Target: {targetVal.toFixed(1)}
                        {isLbs ? "lbs" : "kg"}
                      </div>
                    );
                  }
                  return null;
                })()}
              </Card>
              <Card className="rounded-xl border border-border bg-card p-3 text-center shadow-lg">
                <div className="text-xl font-bold text-foreground">
                  {fitnessData?.preferred_distance_unit === "mi"
                    ? fitnessData?.distance_miles
                      ? fitnessData.distance_miles.toFixed(2)
                      : "--"
                    : fitnessData?.distance_km
                      ? fitnessData.distance_km.toFixed(1)
                      : "--"}
                </div>
                <div className="mb-1 text-xs text-muted-foreground">
                  {fitnessData?.preferred_distance_unit === "mi" ? "mi" : "km"}
                </div>
                <div className="text-xs text-muted-foreground">Distance</div>
              </Card>
              {/* <Card className="rounded-xl border border-border bg-card p-3 text-center shadow-lg">
                <div className="text-xl font-bold text-foreground">
                  {fitnessData?.floors || "--"}
                </div>
                <div className="mb-1 text-xs text-muted-foreground">floors</div>
                <div className="text-xs text-muted-foreground">Climbed</div>
              </Card> */}
            </div>
            <Card
              className="cursor-pointer rounded-2xl border-none bg-gradient-to-br from-pink-100 via-pink-50 to-rose-100 p-5 shadow-lg dark:from-pink-950 dark:via-pink-900 dark:to-rose-950"
              onClick={() => {
                const year = selectedDate.getFullYear();
                const month = String(selectedDate.getMonth() + 1).padStart(
                  2,
                  "0"
                );
                const day = String(selectedDate.getDate()).padStart(2, "0");
                const dateStr = `${year}-${month}-${day}`;
                navigate(`/glucera${GluceraNavigationRoutes.HRV_DETAIL}`, {
                  state: {
                    selectedDate: dateStr,
                    selectedDateObj: selectedDate,
                  },
                });
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="mb-3 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/80 dark:bg-pink-900">
                      <Heart className="h-6 w-6 text-pink-500 dark:text-pink-400" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-foreground">
                        Heart Rate Variability
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-muted-foreground">
                        Last night's reading
                      </p>
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-gray-900 dark:text-foreground">
                      {fitnessData?.hrv_rmssd && fitnessData.hrv_rmssd > 0
                        ? Math.round(fitnessData.hrv_rmssd)
                        : "--"}
                    </span>
                    <span className="text-base text-gray-600 dark:text-muted-foreground">
                      ms
                    </span>
                  </div>
                </div>
                {/* Mini Bar Chart - 7 bars */}
                <div className="flex h-14 items-end gap-1 pt-2">
                  {(() => {
                    // Generate variation based on current HRV if available
                    const baseHrv = fitnessData?.hrv_rmssd || 40;
                    const variation = [0.8, 0.9, 1.1, 0.85, 1.05, 1.15, 1.2];
                    return variation.map((factor, idx) => {
                      const height = Math.min(
                        95,
                        Math.max(50, ((baseHrv * factor) / baseHrv) * 75)
                      );
                      return (
                        <div
                          key={idx}
                          className="w-2 rounded-full bg-pink-400 dark:bg-pink-500"
                          style={{ height: `${height}%` }}
                        />
                      );
                    });
                  })()}
                </div>
              </div>
            </Card>
            {/* Weekly Summary */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <div className="mb-4">
                <h3 className="font-semibold text-foreground">This Week</h3>
              </div>

              <div className="space-y-3">
                {weeklyActivity.map((day, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-8 text-sm font-medium text-muted-foreground">
                      {day.day}
                    </span>
                    <div className="flex-1">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground">
                          {(day?.steps || 0).toLocaleString()}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          steps
                        </span>
                      </div>
                      <Progress
                        value={day.progress}
                        className="h-1.5 bg-border"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 border-t border-border pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Weekly Average
                  </span>
                  <span className="text-lg font-bold text-foreground">
                    {(
                      fitnessData?.weekly_steps?.weekly_average || 0
                    ).toLocaleString()}{" "}
                    steps
                  </span>
                </div>
              </div>
            </Card>

            {/* Health Vitals Header */}
            <div className="mb-4">
              <h3 className="font-semibold text-foreground">Health Vitals</h3>
            </div>

            {/* Health Vitals Cards - Side by Side */}
            <div className="grid grid-cols-3 gap-3">
              {/* SpO2 Card */}
              <Card
                className="cursor-pointer rounded-xl border border-border bg-card p-3 text-center shadow-lg transition-shadow hover:shadow-md"
                onClick={() => {
                  navigate(`/glucera${GluceraNavigationRoutes.SPO2_DETAIL}`, {
                    state: {
                      selectedDate: selectedDate.toISOString().split("T")[0],
                      selectedDateObj: selectedDate,
                      fitnessData: fitnessData,
                    },
                  });
                }}
              >
                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-900">
                  <Droplets className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                </div>
                <div className="text-xl font-bold text-foreground">
                  {fitnessData?.spo2_percent
                    ? `${fitnessData.spo2_percent}%`
                    : "--"}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">SpO2</div>
              </Card>

              {/* Breathing Rate Card */}
              <Card
                className="cursor-pointer rounded-xl border border-border bg-card p-3 text-center shadow-lg transition-shadow hover:shadow-md"
                onClick={() => {
                  navigate(
                    `/glucera${GluceraNavigationRoutes.BREATHING_DETAIL}`,
                    {
                      state: {
                        selectedDate: selectedDate.toISOString().split("T")[0],
                        selectedDateObj: selectedDate,
                        fitnessData: fitnessData,
                      },
                    }
                  );
                }}
              >
                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-green-100 dark:bg-green-900">
                  <Wind className="h-5 w-5 text-green-500 dark:text-green-400" />
                </div>
                <div className="text-xl font-bold text-foreground">
                  {fitnessData?.breathing_rate || "--"}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Breathing Rate
                </div>
              </Card>

              {/* Skin Temperature Card */}
              <Card
                className="cursor-pointer rounded-xl border border-border bg-card p-3 text-center shadow-lg transition-shadow hover:shadow-md"
                onClick={() => {
                  navigate(
                    `/glucera${GluceraNavigationRoutes.TEMPERATURE_DETAIL}`,
                    {
                      state: {
                        selectedDate: selectedDate.toISOString().split("T")[0],
                        selectedDateObj: selectedDate,
                        fitnessData: fitnessData,
                      },
                    }
                  );
                }}
              >
                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-900">
                  <Thermometer className="h-5 w-5 text-orange-500 dark:text-orange-400" />
                </div>
                <div className="text-xl font-bold text-foreground">
                  {fitnessData?.preferred_temperature_unit === "f"
                    ? fitnessData?.skin_temp_fahrenheit
                      ? `${fitnessData.skin_temp_fahrenheit.toFixed(1)}°F`
                      : "--"
                    : fitnessData?.skin_temp_celsius
                      ? `${fitnessData.skin_temp_celsius.toFixed(1)}°C`
                      : "--"}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Skin Temp
                </div>
              </Card>
            </div>
          </>
        )}
      </div>

      <BottomNavBar activeIndex={2} />
    </div>
  );
};

export default FitnessPage;
